// Colour math shared by the shaders (as reference) and CPU code: YCbCr → R'G'B' (C-01..C-03), levels (F-16).

import type { Levels, VideoSpec, ViewAdjustmentValues } from './types';

export type Matrix = 'bt709' | 'bt601' | 'bt2020';
export type Range = 'limited' | 'full';

export interface ColorSetup {
  matrix: Matrix;
  range: Range;
  bitDepth: number;
  /** True when the tags were missing and defaults were used (C-02). */
  defaulted: boolean;
}

/** Normalises WebCodecs/mediabunny matrix names. */
export function normalizeMatrix(m: string | null | undefined): Matrix | undefined {
  if (!m) return undefined;
  const s = m.toLowerCase();
  if (s === 'bt709') return 'bt709';
  if (s === 'bt470bg' || s === 'smpte170m' || s === 'bt601') return 'bt601';
  if (s.startsWith('bt2020')) return 'bt2020';
  return undefined;
}

export function resolveColor(
  spec: Pick<VideoSpec, 'matrixCoefficients' | 'range' | 'height' | 'bitDepth'>,
  override?: { matrixCoefficients?: 'bt709' | 'bt601'; range?: Range },
): ColorSetup {
  const tagged = normalizeMatrix(spec.matrixCoefficients);
  const matrix: Matrix = override?.matrixCoefficients ?? tagged ?? (spec.height >= 720 ? 'bt709' : 'bt601');
  const range: Range = override?.range ?? spec.range ?? 'limited';
  return { matrix, range, bitDepth: spec.bitDepth, defaulted: !tagged && !override?.matrixCoefficients };
}

const KR_KB: Record<Matrix, [number, number]> = {
  bt709: [0.2126, 0.0722],
  bt601: [0.299, 0.114],
  bt2020: [0.2627, 0.0593],
};

/**
 * Returns a 3x4 row-major affine matrix M such that [R',G',B'] = M · [Y, Cb, Cr, 1], where Y/Cb/Cr are
 * code values normalised by (2^bitDepth - 1) and the outputs are 0..1 (unclamped).
 */
export function yuvToRgbMatrix(setup: ColorSetup): number[] {
  const [kr, kb] = KR_KB[setup.matrix];
  const kg = 1 - kr - kb;
  const max = 2 ** setup.bitDepth - 1;
  const scale = 2 ** (setup.bitDepth - 8);
  let yOff: number;
  let yScale: number;
  let cScale: number;
  if (setup.range === 'limited') {
    yOff = (16 * scale) / max;
    yScale = max / (219 * scale);
    cScale = max / (224 * scale);
  } else {
    yOff = 0;
    yScale = 1;
    cScale = 1;
  }
  const cOff = (128 * scale) / max;
  // R = Y' + 2(1-kr) Cr'; B = Y' + 2(1-kb) Cb'; G = Y' - (2 kb (1-kb) Cb' + 2 kr (1-kr) Cr') / kg
  const rCr = 2 * (1 - kr) * cScale;
  const bCb = 2 * (1 - kb) * cScale;
  const gCb = ((-2 * kb * (1 - kb)) / kg) * cScale;
  const gCr = ((-2 * kr * (1 - kr)) / kg) * cScale;
  return [
    yScale, 0, rCr, -yScale * yOff - rCr * cOff,
    yScale, gCb, gCr, -yScale * yOff - gCb * cOff - gCr * cOff,
    yScale, bCb, 0, -yScale * yOff - bCb * cOff,
  ];
}

export function yuvToRgb(setup: ColorSetup, y: number, cb: number, cr: number): [number, number, number] {
  const max = 2 ** setup.bitDepth - 1;
  const m = yuvToRgbMatrix(setup);
  const Y = y / max;
  const U = cb / max;
  const V = cr / max;
  return [
    m[0] * Y + m[1] * U + m[2] * V + m[3],
    m[4] * Y + m[5] * U + m[6] * V + m[7],
    m[8] * Y + m[9] * U + m[10] * V + m[11],
  ];
}

/** 3x4 affine (row-major) → 4x4 row-major. */
function to4(m: number[]): number[] {
  return [...m.slice(0, 4), ...m.slice(4, 8), ...m.slice(8, 12), 0, 0, 0, 1];
}

function mul4(a: number[], b: number[]): number[] {
  const out = new Array<number>(16).fill(0);
  for (let r = 0; r < 4; r++) for (let c = 0; c < 4; c++) for (let k = 0; k < 4; k++) out[r * 4 + c] += a[r * 4 + k] * b[k * 4 + c];
  return out;
}

function inv4(m: number[]): number[] {
  // Gauss-Jordan on a 4x4 row-major matrix.
  const a = m.slice();
  const inv = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
  for (let c = 0; c < 4; c++) {
    let p = c;
    for (let r = c + 1; r < 4; r++) if (Math.abs(a[r * 4 + c]) > Math.abs(a[p * 4 + c])) p = r;
    for (let k = 0; k < 4; k++) {
      [a[c * 4 + k], a[p * 4 + k]] = [a[p * 4 + k], a[c * 4 + k]];
      [inv[c * 4 + k], inv[p * 4 + k]] = [inv[p * 4 + k], inv[c * 4 + k]];
    }
    const d = a[c * 4 + c];
    for (let k = 0; k < 4; k++) {
      a[c * 4 + k] /= d;
      inv[c * 4 + k] /= d;
    }
    for (let r = 0; r < 4; r++) {
      if (r === c) continue;
      const f = a[r * 4 + c];
      for (let k = 0; k < 4; k++) {
        a[r * 4 + k] -= f * a[c * 4 + k];
        inv[r * 4 + k] -= f * inv[c * 4 + k];
      }
    }
  }
  return inv;
}

/**
 * Row-major 4x4 matrix that turns RGB produced by the browser's own conversion (assumed setup) into RGB as our
 * conversion would produce it (S-03 playback path). Identity when both setups agree.
 */
export function correctionMatrix(browser: ColorSetup, ours: ColorSetup): number[] {
  if (browser.matrix === ours.matrix && browser.range === ours.range) return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
  const b = to4(yuvToRgbMatrix({ ...browser, bitDepth: 8 }));
  const o = to4(yuvToRgbMatrix({ ...ours, bitDepth: 8 }));
  return mul4(o, inv4(b));
}

/**
 * The browser's assumption for a frame's colour space. Chrome treats untagged frames as BT.601 limited
 * (measured with T-16 untagged, S-03).
 */
export function browserAssumedSetup(cs: { matrix: string | null; fullRange: boolean | null }): ColorSetup {
  return { matrix: normalizeMatrix(cs.matrix) ?? 'bt601', range: cs.fullRange ? 'full' : 'limited', bitDepth: 8, defaulted: !cs.matrix };
}

/** IRE for a luma code value (limited range: black 0 IRE, white 100 IRE). */
export function lumaToIre(y: number, bitDepth: number, range: Range): number {
  const scale = 2 ** (bitDepth - 8);
  if (range === 'limited') return ((y - 16 * scale) / (219 * scale)) * 100;
  return (y / (2 ** bitDepth - 1)) * 100;
}

export function legalRange(bitDepth: number): { lo: number; hi: number; chromaHi: number } {
  const scale = 2 ** (bitDepth - 8);
  return { lo: 16 * scale, hi: 235 * scale, chromaHi: 240 * scale };
}

export const LEVELS_IDENTITY: Levels = { inBlack: 0, inWhite: 1, outBlack: 0, outWhite: 1, mid: 1 };

/** Levels on a normalised value (0..1). Level fields are normalised too; the UI shows code values (F-16.2). */
export function applyLevels(v: number, l: Levels): number {
  const span = Math.max(1e-6, l.inWhite - l.inBlack);
  let x = Math.min(1, Math.max(0, (v - l.inBlack) / span));
  x = Math.pow(x, 1 / Math.max(0.01, l.mid));
  return l.outBlack + (l.outWhite - l.outBlack) * x;
}

/** Full view-adjustment chain on one channel value (reference for the shader and tests). */
export function applyAdjustment(v: number, adj: ViewAdjustmentValues, channel: 0 | 1 | 2): number {
  let x = v * 2 ** adj.exposureStops;
  x = Math.pow(Math.min(1, Math.max(0, x)), 1 / Math.max(0.01, adj.gamma));
  x = applyLevels(x, adj.levels);
  if (adj.perChannel) {
    const l = channel === 0 ? adj.perChannel.r : channel === 1 ? adj.perChannel.g : adj.perChannel.b;
    x = applyLevels(x, l);
  }
  return x;
}

export function isIdentityAdjustment(adj: ViewAdjustmentValues): boolean {
  const idL = (l: Levels) =>
    l.inBlack === 0 && l.inWhite === 1 && l.outBlack === 0 && l.outWhite === 1 && l.mid === 1;
  return (
    adj.exposureStops === 0 &&
    adj.gamma === 1 &&
    idL(adj.levels) &&
    (!adj.perChannel || (idL(adj.perChannel.r) && idL(adj.perChannel.g) && idL(adj.perChannel.b)))
  );
}

/** Code value ↔ normalised helpers for the Levels UI. */
export function codeToNorm(code: number, bitDepth: number): number {
  return code / (2 ** bitDepth - 1);
}
export function normToCode(v: number, bitDepth: number): number {
  return Math.round(v * (2 ** bitDepth - 1));
}

/** Rec.709 luma weights on R'G'B' for display-side luma (channel view, difference luma-only). */
export const LUMA_709: [number, number, number] = [0.2126, 0.7152, 0.0722];
